import React, {useEffect, useState} from 'react';
import {Box, Chip, CircularProgress, Stack, Tooltip, Typography} from "@mui/material";
import {useNavigate} from 'react-router';
import {subscribeToExperts, getContributionCounts} from '../../firebase/firestore/experts';
import UserAvatar from '../../components/UserAvatar';
import {formatDate} from '../../utils/formatTimestamp';

const relevanceTooltip = 'Computed by AI. Takes into account AoR, comments, and papers authored. Not shared externally.';

function formatRelativeTime(ts) {
    if (!ts) return null;
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths}mo ago`;
}

const ExpertRow = ({expert, contributionCount, onClick}) => {
    const arr = Array.isArray(expert.arr) ? expert.arr : [];
    const paletteKey = expert.relevance === 'high' ? 'success' : expert.relevance === 'medium' ? 'info' : 'warning';
    return (
        <Stack
            direction="row"
            alignItems="center"
            px={2}
            py={1.5}
            bgcolor="white"
            onClick={onClick}
            sx={{cursor: 'pointer', '&:hover': {bgcolor: 'action.hover'}}}
        >
            <Box sx={{width: 40, flexShrink: 0, mr: 2}}>
                <UserAvatar user={expert} userId={expert.uid} sx={{width: 40, height: 40}}/>
            </Box>

            <Stack flex={2} minWidth={0}>
                <Typography fontWeight={600} noWrap>{expert.displayName || expert.name}</Typography>
                <Typography variant="body2" sx={{color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5}} noWrap>
                    {expert.organization}
                    {expert.organization && expert.handle && <span>•</span>}
                    <Box component="img" src={'/assets/jabber.png'} alt="Jabber" sx={{width: 14, height: 14}}/>
                    {expert.handle}
                </Typography>
            </Stack>

            <Stack direction="row" gap={0.75} flex={2} flexWrap="wrap" minWidth={0}>
                {arr.slice(0, 3).map((area, idx) => (
                    <Chip key={idx} label={area} color="primary" variant="outlined" size="small"/>
                ))}
            </Stack>

            <Box flex={1}>
                {expert.relevance ? (
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{
                            width: 10, height: 10, borderRadius: '50%',
                            bgcolor: (theme) => theme.palette[paletteKey]?.main,
                            flexShrink: 0,
                        }}/>
                        <Typography variant="body2" sx={{textTransform: 'capitalize'}}>{expert.relevance}</Typography>
                    </Stack>
                ) : null}
            </Box>

            <Box sx={{width: 110, display: 'flex', justifyContent: 'center', flexShrink: 0}}>
                <Chip
                    label={contributionCount ?? 0}
                    size="small"
                    variant={contributionCount > 0 ? 'filled' : 'outlined'}
                    color={contributionCount > 0 ? 'primary' : 'default'}
                />
            </Box>

            <Box sx={{width: 160, flexShrink: 0, pl: 1}}>
                {expert.expertiseUpdatedAt ? (
                    <Tooltip title={`Profile last rebuilt: ${formatDate(expert.expertiseUpdatedAt, '')}`} placement="top">
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {formatRelativeTime(expert.expertiseUpdatedAt)}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                )}
            </Box>
        </Stack>
    );
};

const ListOfExperts = () => {
    const navigate = useNavigate();
    const [experts, setExperts] = useState(null);
    const [error, setError] = useState(null);
    const [counts, setCounts] = useState({});

    useEffect(() => {
        return subscribeToExperts(
            (data) => setExperts(Array.isArray(data) ? data : []),
            (err) => { console.error('[experts] load failed', err); setError(err); setExperts([]); }
        );
    }, []);

    useEffect(() => {
        getContributionCounts()
            .then(setCounts)
            .catch((err) => console.error('[experts] contribution counts failed', err));
    }, []);

    const sorted = experts ? [...experts].sort((a, b) => {
        const nameA = (a.displayName || a.name || '').toLowerCase();
        const nameB = (b.displayName || b.name || '').toLowerCase();
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    }) : [];

    return (
        <Stack p={2} spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">
                    {experts === null ? 'Experts' : `${experts.length} Expert${experts.length !== 1 ? 's' : ''}`}
                </Typography>
            </Stack>

            {error && (
                <Typography color="error">Failed to load experts. Please try again.</Typography>
            )}

            <Stack
                borderRadius={1}
                border="1px solid #e0e0e0"
                bgcolor="#e0e0e0"
                spacing="1px"
                overflow="hidden"
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" px={2} py={1} bgcolor="#e9ecef">
                    <Box sx={{width: 40, flexShrink: 0, mr: 2}}/>
                    <Typography flex={2} fontWeight="bold">Full Name</Typography>
                    <Tooltip title="Area of Responsibility — the geographic regions or thematic domains this expert covers" placement="top">
                        <Typography flex={2} fontWeight="bold" sx={{cursor: 'default', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3}}>
                            AoR
                        </Typography>
                    </Tooltip>
                    <Tooltip title={relevanceTooltip} placement="top">
                        <Typography flex={1} fontWeight="bold">
                            Estimated Relevance*
                        </Typography>
                    </Tooltip>
                    <Tooltip title="Number of approved FINTELs authored by this expert" placement="top">
                        <Typography sx={{width: 110, flexShrink: 0, textAlign: 'center'}} fontWeight="bold">
                            Contributions
                        </Typography>
                    </Tooltip>
                    <Tooltip title="Date the Expertise Summary was last auto-generated from this expert's published FINTELs" placement="top">
                        <Typography sx={{width: 160, flexShrink: 0, pl: 1}} fontWeight="bold">
                            Last Assessed
                        </Typography>
                    </Tooltip>
                </Stack>

                {/* Rows */}
                {experts === null && (
                    <Stack bgcolor="white" px={2} py={3} alignItems="center">
                        <CircularProgress size={28}/>
                    </Stack>
                )}

                {experts !== null && sorted.length === 0 && (
                    <Stack bgcolor="white" px={2} py={2}>
                        <Typography color="text.secondary">No experts found.</Typography>
                    </Stack>
                )}

                {sorted.map((expert) => (
                    <ExpertRow
                        key={expert.handle || expert.uid}
                        expert={expert}
                        contributionCount={counts[expert.uid] ?? 0}
                        onClick={() => navigate(`/experts/${expert.handle}`)}
                    />
                ))}
            </Stack>
        </Stack>
    );
};

export default ListOfExperts;
